/**
 * @fileoverview Readable defaults derived from the screen's own canvas ground.
 * @module screen-view-v2/utils/canvasForeground
 *
 * WP8 UX M-4 / M-3 (W7). The renderer's defaults assume a light page: a `TapTarget`
 * compiled with `appearance: {}` and a label with no `appearance.color` inherits black, and
 * an embedded custom screen renders on whatever shows through behind it. On the flagship's
 * `#030103` canvas that produced three controls at 1.01:1 and a whole custom screen at
 * 1.1:1 — present, focusable, clickable and invisible.
 *
 * The fix introduces no brand colour. It reads the ground the screen ITSELF declares and
 * picks the higher-contrast of black/white against it, which reproduces today's black on
 * every light canvas and only changes what was unreadable. Every function here is pure:
 * a caller that cannot resolve a ground gets `null`/`undefined` and must change nothing.
 */

export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_LONG = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_WITH_ALPHA = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_FUNC = /^rgba?\(\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*(?:[,/]\s*([0-9.]+)\s*)?\)$/i;

/**
 * Parse an opaque CSS colour into RGB.
 *
 * Returns `null` for anything whose rendered colour cannot be known at this layer:
 * a translucent value (what is behind it is unknown), `transparent`, a `var()`,
 * a named colour, a gradient — all of which mean "do not decide".
 */
export function parseCssColor(input?: string | null): RgbColor | null {
  if (typeof input !== 'string') return null;
  const value = input.trim();
  if (!value) return null;

  const withAlpha = HEX_WITH_ALPHA.exec(value);
  if (withAlpha) {
    return parseInt(withAlpha[4], 16) === 255
      ? {
          r: parseInt(withAlpha[1], 16),
          g: parseInt(withAlpha[2], 16),
          b: parseInt(withAlpha[3], 16),
        }
      : null;
  }

  const long = HEX_LONG.exec(value);
  if (long) {
    return { r: parseInt(long[1], 16), g: parseInt(long[2], 16), b: parseInt(long[3], 16) };
  }

  const short = HEX_SHORT.exec(value);
  if (short) {
    return {
      r: parseInt(short[1] + short[1], 16),
      g: parseInt(short[2] + short[2], 16),
      b: parseInt(short[3] + short[3], 16),
    };
  }

  const func = RGB_FUNC.exec(value);
  if (func) {
    if (func[4] !== undefined && Number(func[4]) < 1) return null;
    const [r, g, b] = [Number(func[1]), Number(func[2]), Number(func[3])];
    if ([r, g, b].some((channel) => channel > 255)) return null;
    return { r, g, b };
  }

  return null;
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2.1 relative luminance. */
export function relativeLuminance(color: RgbColor): number {
  return (
    0.2126 * channelLuminance(color.r) +
    0.7152 * channelLuminance(color.g) +
    0.0722 * channelLuminance(color.b)
  );
}

/** WCAG 2.1 contrast ratio, 1…21. Order-independent. */
export function contrastRatio(a: RgbColor, b: RgbColor): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

/** The two foregrounds the runtime is allowed to choose between. */
const WHITE: RgbColor = { r: 255, g: 255, b: 255 };
const BLACK: RgbColor = { r: 0, g: 0, b: 0 };

/** Minimal shape of `StepContent.canvas` this module reads. */
export interface CanvasLike {
  background?: {
    kind?: string;
    /** Widened: `BackdropContent` is a union (solid | photo | clip); only `solid` has a colour. */
    content?: unknown;
  } | null;
  webViewBackgroundColor?: string;
}

/**
 * The opaque colour the screen puts behind its content, or `null` when it declares none
 * that can be named — a photo or clip backdrop with no web-view colour behind it.
 */
export function resolveCanvasGround(canvas?: CanvasLike | null): string | null {
  if (!canvas) return null;

  const backdrop = canvas.background;
  if (backdrop?.kind === 'solid') {
    const color = (backdrop.content as { color?: string } | null | undefined)?.color;
    if (parseCssColor(color)) return color as string;
  }

  if (parseCssColor(canvas.webViewBackgroundColor)) {
    return canvas.webViewBackgroundColor as string;
  }

  return null;
}

/**
 * `#ffffff` or `#000000` — whichever reads better on `ground`. `null` when the ground
 * cannot be resolved, which the caller must treat as "leave the colour alone".
 *
 * One of the two always clears 4.5:1: the worst case is a mid-grey ground, where both sit
 * near 4.5 and the better one is above it.
 */
export function pickReadableForeground(ground?: string | null): string | null {
  const groundRgb = parseCssColor(ground);
  if (!groundRgb) return null;
  return contrastRatio(WHITE, groundRgb) >= contrastRatio(BLACK, groundRgb) ? '#ffffff' : '#000000';
}

/** Minimal shape of the `StepNode` this module reads. */
export interface StepLike {
  content?: { canvas?: CanvasLike | null } | null;
}

/**
 * The label colour a control on this screen should fall back to when the compiled document
 * names none. `undefined` when the screen declares no resolvable ground.
 */
export function resolveScreenForegroundColor(step?: StepLike | null): string | undefined {
  return pickReadableForeground(resolveCanvasGround(step?.content?.canvas)) ?? undefined;
}

export interface EmbedColors {
  /** Opaque ground the embedded document is given before its own CSS runs. */
  ground: string;
  /** Foreground that reads on that ground. */
  foreground: string;
}

/** The ground an un-themed custom screen is authored against. */
const EMBED_DEFAULT_TEXT = '#111111';
const EMBED_FALLBACK_GROUND = '#ffffff';

/**
 * The ground and foreground an embedded custom screen is given (WP8 UX M-3).
 *
 * An embed renders in its own document (iframe) or under `:host { all: initial }`
 * (shadow DOM), so it inherits neither the funnel's ground nor its foreground and its own
 * CSS is written for a light page. The rule: **keep the funnel's canvas when that canvas
 * can carry the embed's default dark text, otherwise give the embed a white ground of its
 * own.** Blending with the funnel is preferred; being readable is required.
 *
 * The generated CSS hard-coding `#111111` instead of consuming the exposed tokens is the
 * generation half (W8) — this only guarantees there is something readable underneath it.
 */
export function resolveEmbedColors(step?: StepLike | null): EmbedColors {
  const canvasGround = resolveCanvasGround(step?.content?.canvas);
  const canvasRgb = parseCssColor(canvasGround);
  const defaultText = parseCssColor(EMBED_DEFAULT_TEXT)!;

  const ground =
    canvasGround && canvasRgb && contrastRatio(defaultText, canvasRgb) >= 4.5
      ? canvasGround
      : EMBED_FALLBACK_GROUND;

  return { ground, foreground: pickReadableForeground(ground) ?? '#000000' };
}
